import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useAdminShowAdvanced } from '../../../AdminSettingsContext';

interface RoleAction {
  action: string;
  type?: string;
  scope?: string;
  description?: string;
}

interface Role {
  roleName: string;
  actions?: RoleAction[];
}

interface RoleSection {
  roleType: string;
  items: Role[];
}

interface RolePermissionsListProps {
  roleItem: Role;
  roles: RoleSection[] | null;
}

const UNSCOPED_SECTION = 'General';

// Virtual sections we synthesize from admin-* action slugs, splitting
// backend's flat "Program"/"Project" admin bucket into settings tiers.
const PLATFORM_SETTINGS = 'Platform Settings';
const PROGRAM_SETTINGS = 'Program Settings';
const SITE_NAVIGATION = 'Site Navigation';

// Reading order optimized for scanning "what work does this role do?":
// navigation → primary work objects (Projects → Deliverables → Forms →
// Files → Library) → workflow that acts on them → admin settings at the
// bottom (fewest roles have these). Any scope the catalog adds later
// falls through to alphabetical order after these.
const SECTION_ORDER = [
  SITE_NAVIGATION,
  'Project',
  'Deliverable',
  'Deliverable Forms',
  'Attachment',
  'Directory',
  'Workflow',
  PLATFORM_SETTINGS,
  PROGRAM_SETTINGS,
  UNSCOPED_SECTION,
];

const sectionRank = (scope: string) => {
  const idx = SECTION_ORDER.indexOf(scope);
  return idx === -1 ? SECTION_ORDER.length : idx;
};

// Backend groups every admin-* action under Program (or Project for
// admin-ims). For the UI we split them into settings tiers so it's
// obvious which admin bucket a permission belongs to.
const deriveSection = (slug: string, rawScope?: string): string => {
  if (
    slug === 'view-homepage' ||
    slug === 'view-approval-queue' ||
    slug === 'view-library' ||
    slug === 'view-ims-summary' ||
    slug === 'view-financial-views'
  ) {
    return SITE_NAVIGATION;
  }
  // admin-financial-plans is one-off admin without a natural home;
  // fold into Platform Settings so we don't render a single-item
  // Financials section.
  if (
    slug === 'admin-financial-plans' ||
    slug === 'admin-sitewide-access' ||
    slug.startsWith('admin-platform-')
  ) {
    return PLATFORM_SETTINGS;
  }
  if (
    slug === 'admin-ims' ||
    slug.startsWith('admin-manage-') ||
    slug.startsWith('admin-set-')
  ) {
    return PROGRAM_SETTINGS;
  }
  // Backend scopes cancel-submission under Project (it's the "cancel a
  // submitted project deliverable" action), but users think of it as a
  // deliverable-lifecycle action.
  if (slug === 'cancel-submission') return 'Deliverable';
  // Approval-shaped actions belong under the Approvals bucket even when
  // the backend scopes them by the object they operate on (Project,
  // Deliverable, etc.).
  if (
    slug === 'internally-approve-project' ||
    slug === 'internally-approve-deliverable' ||
    slug === 'internally-approve-tar' ||
    slug === 'federally-approve-deliverable'
  ) {
    return 'Workflow';
  }
  return rawScope || UNSCOPED_SECTION;
};

// Display labels for scopes whose UI name diverges from the backend value.
// Backend keeps "Workflow" as the underlying scope; UI just shows "Approvals".
const SECTION_LABELS: Record<string, string> = {
  Workflow: 'Approvals',
  Deliverable: 'Deliverables',
  'Deliverable Forms': 'Forms',
  Attachment: 'Files',
  Directory: 'Library',
  Project: 'Projects',
};

const sectionLabel = (scope: string) => SECTION_LABELS[scope] ?? scope;

// Slug-parts that should render as uppercase acronyms rather than title case.
const ACRONYMS = new Set([
  'IMS',
  'TAR',
  'COR',
  'APM',
  'DPDM',
  'PM',
  'POE',
  'RIP',
]);

// Convert an action slug ("add-deliverable-to-directory") into a
// user-facing label ("Add Deliverable to Directory"). Uppercases known
// acronyms; lowercases small connective words ("to", "of", "and").
const SMALL_WORDS = new Set(['to', 'of', 'and', 'or', 'the', 'a', 'an']);

// Verb-prefix rewrites for engineering slugs that read awkwardly in plain
// English. "get-" and "list-" are the two the ACL uses for read actions;
// "View" / "View All" reads more naturally for non-engineers.
const PREFIX_REWRITES: Array<[string, string]> = [
  ['get-', 'view-'],
  ['list-', 'view-all-'],
];

const rewritePrefix = (slug: string) => {
  for (const [from, to] of PREFIX_REWRITES) {
    if (slug.startsWith(from)) return to + slug.slice(from.length);
  }
  return slug;
};

// Title-case a single slug word, honoring acronyms (TAR → TAR),
// pluralized acronyms (tars → TARs), and small connective words.
const titleCaseWord = (word: string, index: number) => {
  const upper = word.toUpperCase();
  if (ACRONYMS.has(upper)) return upper;
  if (
    upper.length > 1 &&
    upper.endsWith('S') &&
    ACRONYMS.has(upper.slice(0, -1))
  ) {
    return upper.slice(0, -1) + 's';
  }
  if (index > 0 && SMALL_WORDS.has(word)) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
};

// Slug-specific overrides for cases where the generic slug-to-label
// conversion produces awkward text (e.g. singular/plural mismatches).
const ACTION_LABEL_OVERRIDES: Record<string, string> = {
  // Cross-cutting
  'view-financial-views': 'View Financials',
  'view-approval-queue': 'View Approval Queue',
  'view-ims-summary': 'View Schedules',

  // Platform Settings
  'admin-financial-plans': 'Manage Financial Plans',
  'admin-platform-actions': 'Manage Platform Actions',
  'admin-platform-config': 'Manage Platform Configuration',
  'admin-sitewide-access': 'Manage Sitewide Settings',

  // Program Settings
  'admin-set-deliverables': 'Manage Program Deliverable Types',
  'admin-set-workflow-types': 'Manage Program Workflow Types',
  'admin-manage-contacts': 'Manage Program Contacts',
  'admin-manage-locations': 'Manage Program Locations',
  'admin-manage-organizations': 'Manage Program Organizations',
  'admin-manage-program-locations': 'Assign Locations to Program',

  // Projects
  'admin-ims': 'Upload Schedules',
  'archive-project': 'Delete Projects',
  'cancel-submission': 'Cancel Deliverable Workflow',
  'create-project': 'Create Projects',
  'list-projects': 'View Projects',
  'submit-project': 'Submit Projects',
  'update-project': 'Edit Project Details',

  // Deliverables
  'archive-deliverable': 'Delete Deliverables',
  'create-deliverable': 'Create Deliverables',
  // Get/list pair rows collapse into the "list" slug; "All" prefix
  // dropped since it now represents the combined capability.
  'list-inflight-deliverables': 'View Draft Deliverables',
  'list-submitted-deliverables': 'View Submitted Deliverables',
  'submit-deliverable': 'Submit Deliverables',
  'update-inflight-deliverable': 'Edit Deliverable Details',

  // Forms (TAR)
  'archive-tar': 'Delete TARs',
  'create-tar': 'Create TARs',
  'list-inflight-tars': 'View Draft TARs',
  'list-submitted-tars': 'View Submitted TARs',
  'submit-tar': 'Submit New TARs',
  'update-tar': 'Edit TARs',

  // Attachments
  'archive-attachment': 'Delete Files',
  'create-attachment': 'Add Files',
  'list-attachments': 'View Files',
  'update-attachment': 'Edit File Details',

  // Library (Directory)
  'add-deliverable-to-directory': 'Organize Deliverables into Folders',
  'archive-directory': 'Delete Folders',
  'create-directory': 'Create New Folders',
  'list-directories': 'View Folders',
  'rename-directory': 'Rename Folders',
  'update-directory': 'Edit Folder Details',

  // Approvals
  'federally-approve-deliverable': 'Approve Deliverables (Federal)',
  'internally-approve-deliverable': 'Approve Deliverables (Internal)',
  'internally-approve-project': 'Approve Projects (Internal)',
  'internally-approve-tar': 'Approve TARs (Internal)',
  'tar-approval-apm': 'Approve TARs as APM',
  'tar-approval-cor': 'Approve TARs as COR',
  'tar-approval-dpdm': 'Approve TARs as DPDM',
  'tar-approval-pm': 'Approve TARs as PM',
  // Backend workflow step is "site-lead"; UI convention (per team) is to
  // surface this stage as "Program Administrator" instead.
  'tar-approval-site-lead': 'Approve TARs as Program Administrator',
};

const toFriendlyName = (slug: string) =>
  ACTION_LABEL_OVERRIDES[slug] ??
  rewritePrefix(slug).split('-').map(titleCaseWord).join(' ');

// Client-side overrides for backend action-catalog descriptions that have
// grammatical / typo issues. Long-term fix belongs in the backend catalog;
// this map keeps the UI clean until that lands.
const ACTION_DESCRIPTION_OVERRIDES: Record<string, string> = {
  'admin-ims': 'Can upload a new project schedule',
  'admin-set-deliverables':
    'Can manage the collection of Deliverable Types for the Program',
  'admin-sitewide-access':
    'Can manage Program-level site settings in the administrative console',
  'admin-set-workflow-types':
    'Can manage the collection of Workflow Types for the Program',
  // archive-* actions are described as "delete" in the UI even though the
  // backend performs a soft-delete/archive — users think of it as deletion.
  'archive-attachment': 'Can delete attachment files',
  'create-attachment': 'Can add files to an existing deliverable',
  'archive-deliverable': 'Can delete a deliverable',
  'archive-directory': 'Can delete folders',
  'archive-project': 'Can delete a project',
  'archive-tar': 'Can delete a TAR',
  'cancel-submission': 'Can cancel unapproved deliverable requests',
  'submit-project': 'Can request a new project',
  'get-inflight-tar': 'Can view a created but unsubmitted TAR document',
  'federally-approve-deliverable':
    'Can approve deliverables on behalf of the Government',
  'tar-approval-apm': 'Can approve TARs as an APM',
  'tar-approval-cor': 'Can approve TARs as a COR',
  'tar-approval-dpdm': 'Can approve TARs as a Theater Director',
  'tar-approval-pm': 'Can approve TARs as a Tyto PM',
  'tar-approval-site-lead': 'Can approve TARs as a Tyto Program Administrator',
  'internally-approve-deliverable':
    'Can approve deliverables on behalf of Tyto',
  'internally-approve-project': 'Can approve projects on behalf of Tyto',
  'internally-approve-tar': 'Can approve TARs on behalf of Tyto',
  'admin-financial-plans':
    'Can manage financial plans (budgets, forecasts, etc.)',
  'admin-platform-actions':
    'Can manage platform actions in the administrative console',
  'admin-platform-config':
    'Can manage platform configuration settings in the administrative console',
  'admin-manage-program-locations': 'Can associate Locations to the Program',
  'admin-manage-contacts': 'Can manage Program contacts',
  'admin-manage-locations': 'Can manage Program locations',
  'admin-manage-organizations': 'Can manage Program organizations',
  // Trim leftover "available" filler from the auto-substituted "Can list
  // all → Can view all" descriptions on the read actions we haven't
  // manually overridden.
  'list-attachments': 'Can view all files',
  'list-directories': 'Can view all folders in the Library',
  'list-projects': 'Can view all projects',
  // Tighten a few auto-generated descriptions that still reference the
  // engineering scope name.
  'rename-directory': 'Can rename folders in the Library',
  'update-directory': 'Can edit folder details',
  'update-project': 'Can edit project details',
  'submit-deliverable': 'Can submit deliverables into the approval workflow',
  'list-submitted-deliverables': 'Can view all submitted deliverables',
  'list-submitted-tars': 'Can view all submitted TARs',
  'update-tar': 'Can update existing TAR forms',
  'view-ims-summary': 'Can view the Schedule module',
  'view-homepage': 'Can view the Homepage',
  'view-library': 'Can view the Library page',
  'view-approval-queue': 'Can view the Approval Queue module',
  'create-project': 'Can create a new project',
  'list-inflight-deliverables': 'Can view all deliverables in draft',
  'list-inflight-tars': 'Can view all TARs in draft state',
};

const friendlyDescription = (slug: string, raw?: string) => {
  const override = ACTION_DESCRIPTION_OVERRIDES[slug];
  if (override) return override;
  if (!raw) return raw;
  // Backend descriptions lead with "Can list all" or "Can get all" for
  // list/get actions. "Can view all" reads more naturally to end users.
  return raw.replace(/^Can (list|get) all/i, 'Can view all');
};

// get-X / list-Xs pairs represent the same capability from a user's POV
// (see the collection AND see the individual). Backend models them
// separately, but they're always granted together in practice. We render
// only the "list" slug — the "get" slug's row is folded in and its
// granted status contributes to a stricter "both granted" check below.
const PAIR_PARTNER_TO_PRIMARY: Record<string, string> = {
  'get-attachment': 'list-attachments',
  'get-directory': 'list-directories',
  'get-project': 'list-projects',
  'get-inflight-deliverable': 'list-inflight-deliverables',
  'get-submitted-deliverable': 'list-submitted-deliverables',
  'get-inflight-tar': 'list-inflight-tars',
  'get-submitted-tar': 'list-submitted-tars',
  // create-project and submit-project always travel together in prod
  // (nobody's ever granted one without the other) and the business
  // matrix treats them as one capability — "request a new project".
  // Collapse into the create-project row. Same pattern for TAR forms
  // — every role that can create one can also submit it. (Deliverables
  // don't follow this pattern: PM has create-deliverable but not
  // submit-deliverable, so those stay as two rows.)
  'submit-project': 'create-project',
  'submit-tar': 'create-tar',
};
const HIDDEN_PAIR_PARTNERS = new Set(Object.keys(PAIR_PARTNER_TO_PRIMARY));
const PAIR_PRIMARY_TO_PARTNER: Record<string, string> = Object.entries(
  PAIR_PARTNER_TO_PRIMARY,
).reduce<Record<string, string>>((acc, [partner, primary]) => {
  acc[primary] = partner;
  return acc;
}, {});

const RolePermissionsList: React.FC<RolePermissionsListProps> = ({
  roleItem,
  roles,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({});
  const [showMissing, setShowMissing] = useState(false);
  const showAdvanced = useAdminShowAdvanced();

  // Reference set: union of every action seen across every role. First
  // occurrence wins for metadata (scope/description) — the catalog is
  // consistent, so this is a safe fold. Get/list pair partners are
  // filtered out; their row is folded into the primary slug's row below.
  const referenceActions = useMemo<RoleAction[]>(() => {
    const seen = new Map<string, RoleAction>();
    (roles ?? []).forEach((section) => {
      section.items.forEach((role) => {
        (role.actions ?? []).forEach((action) => {
          if (HIDDEN_PAIR_PARTNERS.has(action.action)) return;
          if (!seen.has(action.action)) {
            seen.set(action.action, action);
          }
        });
      });
    });
    return Array.from(seen.values());
  }, [roles]);

  const grantedNames = useMemo(
    () => new Set((roleItem.actions ?? []).map((a) => a.action)),
    [roleItem.actions],
  );

  // A capability is considered granted only if the role has both slugs of
  // the pair (when a pair exists). Any partial state (get without list, or
  // vice versa) surfaces as "not granted" so config bugs are visible.
  const isGranted = (slug: string): boolean => {
    if (!grantedNames.has(slug)) return false;
    const partner = PAIR_PRIMARY_TO_PARTNER[slug];
    if (partner && !grantedNames.has(partner)) return false;
    return true;
  };

  if (referenceActions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No permission catalog available.
      </Typography>
    );
  }

  const grouped = referenceActions.reduce<Record<string, RoleAction[]>>(
    (acc, action) => {
      const section = deriveSection(action.action, action.scope);
      (acc[section] ||= []).push(action);
      return acc;
    },
    {},
  );

  const sections = Object.entries(grouped)
    .map(([scope, items]) => {
      // Granted first, then alphabetical by user-facing label within each
      // group — so a section with a mix reads "what the role has" from
      // the top, and the label ordering matches what the user sees on
      // screen (not the underlying slug).
      const sortedItems = [...items].sort((a, b) => {
        const aGranted = isGranted(a.action);
        const bGranted = isGranted(b.action);
        if (aGranted !== bGranted) return aGranted ? -1 : 1;
        return toFriendlyName(a.action).localeCompare(toFriendlyName(b.action));
      });
      const grantedCount = sortedItems.filter((a) =>
        isGranted(a.action),
      ).length;
      // totalCount reflects every action in the reference catalog for this
      // scope — kept separate from the (possibly filtered) items so the
      // header always reads "granted/total" regardless of the toggle.
      return {
        scope,
        items: sortedItems,
        grantedCount,
        totalCount: sortedItems.length,
      };
    })
    .sort((a, b) => {
      const rank = sectionRank(a.scope) - sectionRank(b.scope);
      return rank !== 0 ? rank : a.scope.localeCompare(b.scope);
    });

  const toggleSection = (scope: string) =>
    setCollapsedSections((prev) => ({ ...prev, [scope]: !prev[scope] }));

  const visibleSections = showMissing
    ? sections
    : sections
        .map((s) => ({
          ...s,
          items: s.items.filter((a) => isGranted(a.action)),
        }))
        .filter((s) => s.items.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Permissions
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showMissing}
              onChange={(e) => setShowMissing(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Show missing permissions
            </Typography>
          }
          sx={{ m: 0 }}
        />
      </Box>
      <Box
        sx={{
          // Container-adaptive grid: at narrow widths (split view) the
          // grid collapses to a single column so each section renders
          // full-width like before. When the container is wide enough
          // (expanded / full-screen view), it fills up to 3 columns of
          // ~360px each, breaking sections into a magazine-style layout
          // and cutting a lot of vertical scroll for role-heavy panels.
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(auto-fill, minmax(340px, 1fr))',
          },
          alignItems: 'start',
          columnGap: 4,
          rowGap: 2.5,
          maxHeight: '50vh',
          overflowY: 'auto',
          pr: 1,
        }}
      >
        {visibleSections.map(({ scope, items, grantedCount, totalCount }) => {
          const collapsed = !!collapsedSections[scope];
          return (
            <Box key={scope}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => toggleSection(scope)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection(scope);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
              >
                {collapsed ? (
                  <KeyboardArrowRightIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                )}
                <Typography
                  variant="overline"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    lineHeight: 1,
                  }}
                >
                  {sectionLabel(scope)} ({grantedCount}/{totalCount})
                </Typography>
              </Box>
              <Collapse in={!collapsed} timeout={200} unmountOnExit>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {items.map((action) => {
                    const granted = isGranted(action.action);
                    return (
                      <Box
                        component="li"
                        key={action.action}
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          py: 0.75,
                          borderBottom: `1px solid ${
                            isDarkMode ? '#333' : '#EEE'
                          }`,
                          '&:last-of-type': { borderBottom: 'none' },
                          opacity: granted ? 1 : 0.65,
                        }}
                      >
                        {granted ? (
                          <CheckCircleIcon
                            color="success"
                            fontSize="small"
                            sx={{ mt: '2px', flexShrink: 0 }}
                          />
                        ) : (
                          <CancelIcon
                            color="error"
                            fontSize="small"
                            sx={{ mt: '2px', flexShrink: 0 }}
                          />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              textDecoration: granted ? 'none' : 'line-through',
                            }}
                          >
                            {toFriendlyName(action.action)}
                          </Typography>
                          {(() => {
                            const desc = friendlyDescription(
                              action.action,
                              action.description,
                            );
                            return desc ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {desc}
                              </Typography>
                            ) : null;
                          })()}
                          {showAdvanced && (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', color: '#1F4D83' }}
                            >
                              {PAIR_PRIMARY_TO_PARTNER[action.action]
                                ? `Roles: ${action.action} | ${PAIR_PRIMARY_TO_PARTNER[action.action]}`
                                : `Role: ${action.action}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default RolePermissionsList;
